import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import CheckShow from "../shared/CheckShow";
import DropdownButton from "../DropdownButton";

import {
    CATEGORY,
    getEventSelectionKey,
    resolveOwnerLabel,
} from "../../helpers/events";
import { formatDateRange, newDateUTC } from "../../helpers/dates";
import Colours from "../../helpers/colours";
import Icons from "../../helpers/icons";
import { ARO_ALL } from "../../configuration";
import { EventOccurrencesViewerPill } from "./EventOccurrencesViewer";

const EventsTable = ({
    handleDelete,
    eventsExtra,
    events,
    loading,
    handleEventClicked,
    showActions = true,
    enableBulkSelection = false,
    isAdmin = false,
    onBulkDelete = () => {},
    onBulkDuplicate = () => {},
    maxHeight = 600,
    emptyMessage = "No events found matching your criteria.",
}) => {
    const [selectedEventIDs, setSelectedEventIDs] = useState([]);

    const visibleEventIDs = useMemo(
        () => events.map((ev) => ev.EventID),
        [events],
    );

    const selectedVisibleCount = useMemo(
        () =>
            selectedEventIDs.filter((id) => visibleEventIDs.includes(id))
                .length,
        [selectedEventIDs, visibleEventIDs],
    );

    const allVisibleSelected =
        visibleEventIDs.length > 0 &&
        selectedVisibleCount === visibleEventIDs.length;

    const selectedEvents = useMemo(
        () =>
            events.filter((event) => selectedEventIDs.includes(event.EventID)),
        [events, selectedEventIDs],
    );

    useEffect(() => {
        if (!isAdmin) {
            setSelectedEventIDs([]);
            return;
        }

        setSelectedEventIDs((prev) => {
            const next = prev.filter((id) => visibleEventIDs.includes(id));
            if (
                next.length === prev.length &&
                next.every((id, index) => id === prev[index])
            ) {
                return prev;
            }

            return next;
        });
    }, [visibleEventIDs, isAdmin]);

    const handleToggleEventSelection = useCallback((event, checked) => {
        const normalizedID = event.EventID;

        setSelectedEventIDs((prev) => {
            if (checked) {
                if (prev.includes(normalizedID)) return prev;
                return [...prev, normalizedID];
            }

            return prev.filter((id) => id !== normalizedID);
        });
    }, []);

    const handleToggleSelectAllVisible = useCallback(
        (checked) => {
            if (!checked) {
                setSelectedEventIDs((prev) =>
                    prev.filter((id) => !visibleEventIDs.includes(id)),
                );
                return;
            }

            setSelectedEventIDs((prev) => {
                const next = new Set(prev);
                visibleEventIDs.forEach((id) => next.add(id));
                return Array.from(next);
            });
        },
        [visibleEventIDs],
    );
    const SETTINGS = [
        {
            title: "Edit",
            icon: Icons.Edit,
            onClick: (event) => {
                handleEventClicked(event, "edit");
            },
        },
        ...CheckShow({
            rolesThatCanSee: ARO_ALL,
            children: [
                {
                    title: "Delete",
                    icon: Icons.Delete,
                    onClick: (event) => {
                        handleDelete(event);
                    },
                },
            ],
            alternative: [],
        }),
        {
            title: "Copy",
            icon: Icons.Copy,
            onClick: (event) => {
                handleEventClicked(event, "copy");
            },
        },
    ];

    const truncateWithReadMore = (text, readMore = true) => {
        const maxLength = 16;
        const isTruncated = typeof text === "string" && text.length > maxLength;
        return (
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: 0.5,
                }}
            >
                <Typography component="span">
                    {isTruncated ? text.substring(0, maxLength) + "..." : text}
                </Typography>
                {isTruncated && readMore && (
                    <Typography
                        variant="caption"
                        component="span"
                        fontWeight={"bold"}
                        sx={{
                            color: "primary.main",
                            cursor: "pointer",
                            "&:hover": { color: "primary.dark" },
                        }}
                    >
                        Read more
                    </Typography>
                )}
            </Box>
        );
    };

    const BASE_HEADER = [
        {
            id: "FromDate",
            label: "DATE",
            width: "160px",
            format: (event) => (
                <Stack direction={"column"} spacing={1}>
                    <Typography>
                        {formatDateRange(event["FromDate"], event["ToDate"])}
                    </Typography>
                    {event.RepeatRule && (
                        <EventOccurrencesViewerPill
                            rrule={event["RepeatRule"]}
                            start={event["FromDate"]}
                            end={event["ToDate"]}
                            repeatStart={event["RepeatStart"]}
                        />
                    )}
                </Stack>
            ),
        },

        {
            id: "Name",
            label: "NAME",
            width: "22%",
            format: (event) => event.Name,
        },
        {
            id: "Category",
            label: "CATEGORY",
            width: "22%",
            format: (event) => {
                let cat = event.Category;
                let subcat = event.Subcategory;

                return (
                    <Stack direction={"column"} spacing={1}>
                        <Typography
                            sx={{
                                p: 1,
                                paddingX: 2,
                                m: 1,
                                ml: 0,
                                borderRadius: 10,
                                width: "fit-content",
                                border: "2px solid grey",
                            }}
                        >
                            {CATEGORY.hasOwnProperty(cat)
                                ? CATEGORY[cat].label
                                : cat}
                        </Typography>
                        {subcat && truncateWithReadMore(subcat, false)}
                    </Stack>
                );
            },
        },
        {
            id: "Owner",
            label: "OWNER",
            width: "15%",
            format: (event) => {
                return (
                    <Typography>
                        {resolveOwnerLabel(
                            event.Owner,
                            eventsExtra?.Contacts ?? {},
                        )}
                    </Typography>
                );
            },
        },
    ];

    const HEADER = showActions
        ? [...BASE_HEADER, { label: "ACTIONS", id: "Settings", width: "80px" }]
        : BASE_HEADER;

    const FINAL_HEADER = enableBulkSelection
        ? [{ label: "", id: "BulkSelection", width: "48px" }, ...HEADER]
        : HEADER;

    return (
        <>
            {enableBulkSelection && selectedVisibleCount > 0 && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mt: 1,
                        mb: 2,
                        borderRadius: 2,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                    >
                        <Typography fontWeight="bold">
                            {selectedVisibleCount} event
                            {selectedVisibleCount > 1 ? "s" : ""} selected
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                disabled={selectedVisibleCount !== 1}
                                onClick={() =>
                                    onBulkDuplicate(selectedEvents, {
                                        onComplete: ({
                                            failedEventIDs = [],
                                        }) => {
                                            setSelectedEventIDs(failedEventIDs);
                                        },
                                    })
                                }
                            >
                                Duplicate event
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{ color: "error.main" }}
                                onClick={() => {
                                    onBulkDelete(selectedEvents, {
                                        onComplete: ({ failedEvents = [] }) => {
                                            setSelectedEventIDs(
                                                failedEvents.map(
                                                    (ev) => ev.EventID,
                                                ),
                                            );
                                        },
                                    });
                                }}
                                startIcon={<Icons.Delete />}
                            >
                                Delete
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}
            <TableContainer
                component={Paper}
                elevation={3}
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    mt: 1,
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        maxHeight: `${maxHeight}px`,
                        overflowY: "auto",
                        display: "block",
                    }}
                >
                    <Table
                        sx={{
                            borderCollapse: "separate",
                            borderSpacing: 0,
                            tableLayout: "fixed",
                        }}
                    >
                        <TableHead
                            sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                backgroundColor: Colours.lightBubble,
                            }}
                        >
                            <TableRow>
                                {FINAL_HEADER.map((headCell, index) => (
                                    <TableCell
                                        key={headCell.id}
                                        sx={{
                                            ...(headCell.width && {
                                                width: headCell.width,
                                            }),
                                        }}
                                    >
                                        {headCell.id === "BulkSelection" ? (
                                            <Checkbox
                                                size="small"
                                                checked={allVisibleSelected}
                                                indeterminate={
                                                    selectedVisibleCount > 0 &&
                                                    !allVisibleSelected
                                                }
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleSelectAllVisible(
                                                        e.target.checked,
                                                    );
                                                }}
                                                inputProps={{
                                                    "aria-label":
                                                        "select all events",
                                                }}
                                            />
                                        ) : (
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: Colours.graphDisabled,
                                                }}
                                            >
                                                {headCell.label}
                                            </Typography>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={FINAL_HEADER.length}>
                                        {"Loading events..."}
                                    </TableCell>
                                </TableRow>
                            ) : events.length > 0 ? (
                                events.map((event, index) => {
                                    return (
                                        <TableRow
                                            key={event.EventID}
                                            onClick={() => {
                                                handleEventClicked(event);
                                            }}
                                            sx={{
                                                cursor: "pointer",
                                            }}
                                        >
                                            {FINAL_HEADER.map(
                                                (
                                                    { id, format, style },
                                                    cellIndex,
                                                ) => {
                                                    let val = event;

                                                    return (
                                                        <TableCell
                                                            key={`${event.EventID}-${id}`}
                                                            sx={{
                                                                ...(id ===
                                                                    "FromDate" && {
                                                                    borderLeft: `4px solid ${CATEGORY[event.Category]?.color}`,
                                                                }),
                                                                ...(id ===
                                                                    "Settings" && {
                                                                    textAlign:
                                                                        "center",
                                                                    verticalAlign:
                                                                        "middle",
                                                                }),
                                                                ...(id ===
                                                                    "Name" && {
                                                                    wordBreak:
                                                                        "break-word",
                                                                    overflowWrap:
                                                                        "break-word",
                                                                }),
                                                            }}
                                                        >
                                                            {id ===
                                                            "BulkSelection" ? (
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={selectedEventIDs.includes(
                                                                        event.EventID,
                                                                    )}
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleToggleEventSelection(
                                                                            event,
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        );
                                                                    }}
                                                                    inputProps={{
                                                                        "aria-label": `select event ${event.EventID}`,
                                                                    }}
                                                                />
                                                            ) : id ===
                                                              "Settings" ? (
                                                                <DropdownButton
                                                                    header={{
                                                                        closeButton: false,
                                                                    }}
                                                                    iconButton
                                                                    closeOnItemClick
                                                                    Icon={
                                                                        Icons.Options
                                                                    }
                                                                >
                                                                    {SETTINGS.map(
                                                                        (
                                                                            sett,
                                                                        ) => (
                                                                            <MenuItem
                                                                                key={
                                                                                    sett.title
                                                                                }
                                                                                disabled={
                                                                                    sett?.disabled
                                                                                        ? sett.disabled(
                                                                                              event,
                                                                                          )
                                                                                        : false
                                                                                }
                                                                                onClick={(
                                                                                    e,
                                                                                ) => {
                                                                                    e.stopPropagation();

                                                                                    sett.onClick(
                                                                                        event,
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <Stack
                                                                                    direction="row"
                                                                                    spacing={
                                                                                        1
                                                                                    }
                                                                                    alignItems="center"
                                                                                >
                                                                                    {sett.icon && (
                                                                                        <sett.icon fontSize="small" />
                                                                                    )}
                                                                                    <span>
                                                                                        {
                                                                                            sett.title
                                                                                        }
                                                                                    </span>
                                                                                </Stack>
                                                                            </MenuItem>
                                                                        ),
                                                                    )}
                                                                </DropdownButton>
                                                            ) : (
                                                                format(val)
                                                            )}
                                                        </TableCell>
                                                    );
                                                },
                                            )}
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={FINAL_HEADER.length}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>
        </>
    );
};

export default EventsTable;
